import { Link } from '@rocketlane/rocket-router';
import styled, { createGlobalStyle, css } from 'styled-components';
export const SIDEBAR_EXPANDED_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 52;
export const SidebarRoot = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ $collapsed })=>$collapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_EXPANDED_WIDTH}px`};
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--scarlet-gray-10);
  border-right: 1px solid var(--scarlet-gray-60);
  padding: var(--padding-01);
  transition: width var(--animation-duration-medium) ease;
`;
export const SidebarScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: var(--padding-xs);
  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: var(--padding-xs);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--black-20);
    border-radius: var(--border-radius-base);
  }
`;
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $collapsed })=>$collapsed ? '0' : 'var(--padding-01)'};
  width: 100%;
  flex-shrink: 0;
  justify-content: ${({ $collapsed })=>$collapsed ? 'center' : 'flex-start'};
`;
export const BrandBlock = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-start;
  gap: var(--padding-01);
  height: var(--height-row);
  padding: var(--padding-xxs) var(--padding-01);
  border-radius: var(--border-radius-default);
  text-align: left;
`;
export const BrandLogo = styled.img`
  width: var(--avatar-size-sm);
  height: var(--avatar-size-sm);
  flex-shrink: 0;
  border-radius: var(--border-radius-default);
  display: block;
`;
/** Rocketlane brand mark for "Back to Rocketlane" — same box as sibling nav icons. */ export const RocketlaneBackMark = styled.img`
  width: var(--height-xs);
  height: var(--height-xs);
  display: block;
  flex-shrink: 0;
  object-fit: contain;
`;
export const BrandTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-family: var(--font-family);
  font-weight: var(--heading-6-weight);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-07);
  color: var(--text-color);
`;
export const CollapseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--height-sm);
  height: var(--height-sm);
  padding: 0;
  border: none;
  border-radius: var(--border-radius-small);
  background: var(--button-secondary);
  color: var(--icon-primary);
  cursor: pointer;

  &:hover {
    background: var(--scarlet-gray-40);
  }

  svg {
    width: var(--height-xs);
    height: var(--height-xs);
  }
`;
/** Collapsed header: logo only; expand control appears on hover in the same slot. */ export const CollapsedBrandHitArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--height-row);
  height: var(--height-row);
  flex-shrink: 0;
  border-radius: var(--border-radius-default);

  ${BrandLogo} {
    transition: opacity var(--animation-duration-fast) ease;
  }

  ${CollapseButton} {
    position: absolute;
    inset: var(--padding-xs);
    width: var(--height-sm);
    height: var(--height-sm);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--animation-duration-fast) ease;
  }

  &:hover ${BrandLogo}, &:focus-within ${BrandLogo} {
    opacity: 0;
  }

  &:hover ${CollapseButton}, &:focus-within ${CollapseButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;
export const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ $collapsed })=>$collapsed ? 'var(--spacing-03)' : 'var(--spacing-02)'};
  padding: ${({ $collapsed })=>$collapsed ? '0' : 'var(--spacing-03) 0'};
  flex-shrink: 0;

  &:first-of-type {
    padding-top: ${({ $collapsed })=>$collapsed ? 'var(--spacing-03)' : 'var(--spacing-05)'};
  }
`;
export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  height: var(--height-header);
  padding: var(--padding-xxs) var(--padding-01);
  font-weight: var(--heading-7-weight);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-02);
  letter-spacing: 0.16px;
  color: var(--text-02);
  white-space: nowrap;
`;
const navRowShell = css`
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px;
  border-radius: var(--border-radius-4);
  background: ${({ $active })=>$active ? 'var(--scarlet-gray-40, #f2f2f2)' : 'transparent'};
  color: var(--text-primary, #161616);

  &:hover {
    background: ${({ $active })=>$active ? 'var(--scarlet-gray-40)' : 'var(--scarlet-gray-20)'};
  }
`;
export const NavRow = styled.button`
  ${navRowShell};
  gap: 8px;
  padding: 2px 8px;
  border: none;
  cursor: pointer;
  text-align: left;
`;
export const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--height-xs);
  height: var(--height-xs);
  color: var(--icon-primary);

  svg {
    width: var(--height-xs);
    height: var(--height-xs);
    font-size: var(--height-xs);
  }
`;
export const AgentIcon = styled.img`
  width: var(--height-xs);
  height: var(--height-xs);
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;
export const NavLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-base);
  line-height: var(--line-height-body-short-01);
  font-weight: var(--font-weight-normal);
`;
export const AgentNavRow = styled.div`
  ${navRowShell};
  gap: var(--spacing-02);
  padding: 0px var(--spacing-02) 0px var(--spacing-03);
`;
export const AgentNavMain = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
`;
export const AgentChildNavRow = styled.button`
  ${navRowShell};
  gap: var(--spacing-03);
  padding: 0px var(--spacing-03) 0px var(--spacing-07);
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${({ $active })=>$active ? 'var(--text-primary, #161616)' : 'var(--text-secondary, #525252)'};
`;
export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: var(--height-xs);
  padding: 0 var(--padding-xs);
  border-radius: var(--border-radius-small);
  background: var(--red-30);
  color: var(--red-70);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-helper-01);
  font-weight: var(--font-weight-normal);
`;
export const ChatsEmpty = styled.div`
  padding: var(--padding-xs) var(--padding-01);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-helper-01);
  color: var(--text-02);
`;
export const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--padding-xs) var(--padding-01);
  border: none;
  border-radius: var(--border-radius-default);
  background: transparent;
  font-size: var(--font-size-base);
  line-height: var(--line-height-body-short-01);
  font-weight: var(--font-weight-normal);
  color: var(--text-02);
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--scarlet-gray-20);
  }
`;
export const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: var(--padding-01);
  width: 100%;
  margin-top: auto;
  padding-top: var(--padding-01);
  align-items: ${({ $collapsed })=>$collapsed ? 'center' : 'stretch'};
`;
export const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed })=>$collapsed ? 'center' : 'space-between'};
  gap: var(--padding-01);
  width: ${({ $collapsed })=>$collapsed ? 'var(--height-row)' : '100%'};
  min-height: ${({ $collapsed })=>$collapsed ? 'var(--height-row)' : 'var(--input-height-lg)'};
  padding: ${({ $collapsed })=>$collapsed ? '0' : 'var(--padding-01) var(--padding-sm)'};
  border: 1px solid var(--scarlet-gray-60);
  border-radius: ${({ $collapsed })=>$collapsed ? '50%' : 'var(--border-radius-small)'};
  background: var(--white-100);
  color: var(--text-color);
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled) {
    background: var(--scarlet-gray-20);
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;
export const UpdateCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: var(--padding-xxs);
  min-width: 0;
  flex: 1;
`;
export const UpdateTitle = styled.span`
  font-size: var(--font-size-base);
  line-height: var(--line-height-02);
  font-weight: var(--heading-6-weight);
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const UpdateSubtitle = styled.span`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-helper-01);
  font-weight: var(--font-weight-normal);
  color: var(--text-02);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const UpdateIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $collapsed })=>$collapsed ? 'var(--height-row)' : 'var(--height-sm)'};
  height: ${({ $collapsed })=>$collapsed ? 'var(--height-row)' : 'var(--height-sm)'};
  color: var(--icon-primary);

  svg {
    width: var(--height-xs);
    height: var(--height-xs);
    font-size: var(--height-xs);
  }
`;
export const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed })=>$collapsed ? 'center' : 'flex-start'};
  gap: var(--padding-01);
  width: ${({ $collapsed })=>$collapsed ? 'var(--height-row)' : '100%'};
  min-height: var(--height-lg);
  padding: ${({ $collapsed })=>$collapsed ? '0' : 'var(--padding-xs) var(--padding-01)'};
  border: none;
  border-radius: var(--border-radius-small);
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--scarlet-gray-20);
  }
`;
export const ProfileAvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
`;
export const PresenceDot = styled.span`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: var(--height-xxs);
  height: var(--height-xxs);
  border-radius: 50%;
  background: var(--approve-color);
  border: 1.5px solid var(--scarlet-gray-10);
  box-sizing: border-box;
`;
export const ProfileMeta = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;
export const ProfileName = styled.span`
  font-size: var(--font-size-base);
  line-height: var(--line-height-02);
  font-weight: var(--heading-6-weight);
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const ProfileSubtitle = styled.span`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-helper-01);
  font-weight: var(--font-weight-normal);
  color: var(--text-02);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const PROFILE_POPOVER_OVERLAY_CLASS = 'nitro-claw-desktop-sidebar-profile-popover';
export const COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS = 'nitro-claw-desktop-sidebar-collapsed-flyout';
export const ProfilePopoverOverlayGlobalStyle = createGlobalStyle`
  .${PROFILE_POPOVER_OVERLAY_CLASS}.ant-popover .ant-popover-inner-content {
    padding: 0;
  }
`;
export const CollapsedNavFlyoutOverlayGlobalStyle = createGlobalStyle`
  .${COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS}.ant-popover .ant-popover-inner-content {
    padding: 0;
  }

  .${COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS}.ant-popover .ant-popover-arrow {
    display: none;
  }
`;
export const CollapsedFlyoutPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
  min-width: 220px;
  max-width: 280px;
  padding: var(--spacing-02);
`;
export const CollapsedFlyoutHeader = styled.div.attrs({
    className: 'heading-compact-00'
})`
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0px var(--spacing-03);
  color: var(--gray-40);
  white-space: nowrap;
`;
export const CollapsedFlyoutItem = styled.button`
  ${navRowShell};
  gap: var(--spacing-03);
  padding: 0px var(--spacing-03);
  border: none;
  cursor: pointer;
  text-align: left;
`;
export const ProfileMenuPanel = styled.div`
  min-width: 140px;
  padding: var(--padding-xs);
`;
export const ProfileLogoutLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--padding-01);
  width: 100%;
  padding: var(--padding-01) var(--padding-sm);
  border-radius: var(--border-radius-default);
  font-size: var(--font-size-base);
  line-height: var(--line-height-body-short-01);
  font-weight: var(--font-weight-normal);
  color: var(--error-color);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: var(--ozone-red-surface);
    color: var(--error-color);
  }

  svg {
    width: var(--font-size-base);
    height: var(--font-size-base);
    font-size: var(--font-size-base);
    color: inherit;
  }
`;
